/**
 * Chart drawings: the model and the lightweight-charts plugin that renders them.
 *
 * lightweight-charts has no built-in drawing tools — TradingView keeps those for its
 * paid widget. What v4 does give us is the *primitive* API: an object attached to a
 * series that gets a pane renderer plus the series' own coordinate converters. That is
 * all a trendline needs, and it means drawings live in chart space (time, price)
 * rather than pixels — so they stay glued to the data through zoom, pan and resize,
 * which a naive canvas overlay would not.
 */

import type {
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  ISeriesPrimitivePaneRenderer,
  ISeriesPrimitivePaneView,
  SeriesAttachedParameter,
  Time,
} from "lightweight-charts";

export type DrawingKind = "trendline" | "horizontal";

export interface Drawing {
  id?: string;
  kind: DrawingKind;
  /** Chart coordinates, not pixels — see the module note. */
  p1: { time: number; price: number };
  /** For a horizontal level p2 is ignored on the time axis. */
  p2: { time: number; price: number };
  color?: string;
  updated_at?: number;
}

const DEFAULT_COLOR = "#4c8dff";

/** A primitive that paints every drawing for one series. */
export class DrawingLayer implements ISeriesPrimitive<Time> {
  private drawings: Drawing[] = [];
  private chart: IChartApi | null = null;
  private series: ISeriesApi<"Candlestick"> | null = null;
  private requestUpdate?: () => void;
  /** The in-progress line while the user drags, drawn but not yet committed. */
  private preview: Drawing | null = null;

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart as IChartApi;
    this.series = param.series as ISeriesApi<"Candlestick">;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chart = null;
    this.series = null;
  }

  set(drawings: Drawing[]): void {
    this.drawings = drawings;
    this.requestUpdate?.();
  }

  setPreview(d: Drawing | null): void {
    this.preview = d;
    this.requestUpdate?.();
  }

  all(): Drawing[] {
    return this.drawings;
  }

  paneViews(): readonly ISeriesPrimitivePaneView[] {
    const items = this.preview ? [...this.drawings, this.preview] : this.drawings;
    const chart = this.chart;
    const series = this.series;

    const renderer: ISeriesPrimitivePaneRenderer = {
      draw: (target) => {
        if (!chart || !series) return;
        // Bitmap space, not CSS space: on a HiDPI display the canvas has more device
        // pixels than CSS pixels, and ignoring the ratio draws blurred, offset lines.
        target.useBitmapCoordinateSpace((scope) => {
          const ctx = scope.context;
          const hr = scope.horizontalPixelRatio;
          const vr = scope.verticalPixelRatio;
          const timeScale = chart.timeScale();
          ctx.save();
          ctx.lineWidth = 1.5 * hr;
          for (const d of items) {
            const y1 = series.priceToCoordinate(d.p1.price);
            if (y1 === null) continue;
            ctx.strokeStyle = d.color ?? DEFAULT_COLOR;

            if (d.kind === "horizontal") {
              // A level spans the whole pane, so it needs no x coordinates and stays
              // visible even when its anchor scrolls off-screen.
              ctx.beginPath();
              ctx.setLineDash([4 * hr, 4 * hr]);
              ctx.moveTo(0, y1 * vr);
              ctx.lineTo(scope.bitmapSize.width, y1 * vr);
              ctx.stroke();
              ctx.setLineDash([]);
              continue;
            }

            const x1 = timeScale.timeToCoordinate(d.p1.time as Time);
            const x2 = timeScale.timeToCoordinate(d.p2.time as Time);
            const y2 = series.priceToCoordinate(d.p2.price);
            if (x1 === null || x2 === null || y2 === null) continue;
            ctx.beginPath();
            ctx.moveTo(x1 * hr, y1 * vr);
            ctx.lineTo(x2 * hr, y2 * vr);
            ctx.stroke();
          }
          ctx.restore();
        });
      },
    };

    return [{ renderer: () => renderer }];
  }
}

export const drawingsApi = {
  async load(ticker: string): Promise<Drawing[]> {
    const res = await fetch(`/api/v1/drawings/${encodeURIComponent(ticker)}`, {
      credentials: "include",
    });
    if (!res.ok) return [];
    return ((await res.json()) as { drawings: Drawing[] }).drawings ?? [];
  },
  async save(ticker: string, drawings: Drawing[]): Promise<Drawing[]> {
    const res = await fetch(`/api/v1/drawings/${encodeURIComponent(ticker)}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ drawings }),
    });
    if (!res.ok) throw new Error(`save failed: ${res.status}`);
    return ((await res.json()) as { drawings: Drawing[] }).drawings ?? [];
  },
};
